import { useCallback, useState } from 'react';
import { maestroApiCall, useTranslation } from '@michelin/central-provider';
import GLOBAL_CONFIG from 'global-variables';

export const usePresignedUpload = ({ fleetId }: { fleetId: string }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const baseURL = GLOBAL_CONFIG.externalAPIUrl;

  const getFileUploadURL = useCallback(
    () => maestroApiCall({ method: 'POST', endpoint: `cp-be/file-uploads/${fleetId}`, baseURL }),
    [fleetId, baseURL],
  );

  const reset = useCallback(() => {
    setIsLoading(false);
    setUploadedFileId(null);
    setError('');
    setAbortController(null);
  }, []);

  const abortUpload = useCallback(() => {
    abortController?.abort();
    setIsLoading(false);
    setAbortController(null);
  }, [abortController]);

  const startUpload = useCallback(
    async (file: File): Promise<string | null> => {
      setError('');
      setUploadedFileId(null);
      setIsLoading(true);
      const localAbort = new AbortController();
      setAbortController(localAbort);

      let uploadInfo: any;
      try {
        uploadInfo = await getFileUploadURL();
        if (!uploadInfo?.url) throw new Error('Missing URL');
      } catch {
        setIsLoading(false);
        setAbortController(null);
        setError(t('There was an error preparing upload. Please try again later'));
        return null;
      }

      try {
        await fetch(uploadInfo.url, {
          method: 'PUT',
          body: file,
          mode: 'cors',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          signal: localAbort.signal,
        });
      } catch (e) {
        setIsLoading(false);
        setAbortController(null);
        if ((e as any)?.code === 20) return null; // aborted; do not treat as error
        setError(t('There was an error uploading the file. Please try again later'));
        return null;
      }

      setIsLoading(false);
      setAbortController(null);
      setUploadedFileId(uploadInfo.fileID);
      return uploadInfo.fileID || null;
    },
    [getFileUploadURL, t],
  );

  return {
    isLoading,
    uploadedFileId,
    error,
    startUpload,
    abortUpload,
    reset,
  };
};
